/**
 * CoworkBuildAccordion — the "Guaranteed to accomplish in just 3.5 hours"
 * section on the Claude Cowork landing page.
 *
 * Reuses the single-open accordion interaction pattern from ToolkitAccordion
 * (uncontrolled, max-height animation) but with the
 * Cowork layout: each item is a soft-beige rounded card whose header shows a clay
 * PNG icon on the LEFT, the build title, and a +/× toggle on the RIGHT. The
 * expanded panel is a two-column layout — body copy on the left, a screenshot on
 * the right (placeholder library images for now until real captures land).
 *
 * Inline styles only (no index.css) — matches ToolkitAccordion's approach and
 * keeps this self-contained.
 */
import { DsIcon, DS_ICON_PREFIX } from "./ds-icons";
import { useState, useRef, useEffect, Fragment, type ReactNode } from "react";

export interface CoworkBuildItem {
  icon: string;
  headline: string;
  /** Body copy. `\n\n` splits paragraphs; `[[word]]` renders a terra
   *  hand-drawn underline that draws when the panel is open. */
  description: string;
  /** Right-column screenshot. OPTIONAL: a card with no honest capture yet ships
   *  without one rather than borrowing a screenshot of a different product. The
   *  row then runs as a single text column (see the grid override below), which
   *  reads as a deliberate layout, not a missing image. */
  image?: string;
  /** Live right-column visual (e.g. an animated demo). Takes the image's place
   *  when set; pages that don't pass it render exactly as before. */
  media?: ReactNode;
  /** Optional muted loop (mp4) played over `image`, which stays as its poster
   *  and as the reduced-motion fallback. The AI Video page uses it on the
   *  "moves naturally" card, where a still would undercut the point. */
  video?: string;
}

/**
 * Renders body text with `[[word]]` tokens as terra marker highlights.
 * Mostly straight with a LITTLE hand (per Isaac): one clean swipe, slightly
 * tilted, gently skewed marker ends, uneven corner radii. Drawn by a ::before
 * overlay (see .cba-hl rules in the component's <style> block) that scales in
 * left-to-right when the panel opens. inline-block keeps each phrase on one
 * line so the overlay never fragments across wraps.
 */
function AnnotatedText({ text, show }: { text: string; show: boolean }) {
  const parts = text.split(/(\[\[[^\]]+\]\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = part.match(/^\[\[([^\]]+)\]\]$/);
        if (!m) return <Fragment key={i}>{part}</Fragment>;
        return (
          <span key={i} className="cba-hl" data-show={show ? "true" : "false"}>
            {m[1]}
          </span>
        );
      })}
    </>
  );
}

const CARD_BG = "hsl(47, 33%, 95%)"; // soft beige, a touch lighter than cream
const CARD_BORDER = "1px solid var(--charcoal-14)";
const RADIUS = "14px";

// Shared layout rhythm. TITLE_INSET = card padding + icon + gap, so the expanded
// body copy lines up under the header title (not under the icon).
const PAD_X = "clamp(20px, 2.8vw, 30px)";
const GAP = "clamp(16px, 2.4vw, 24px)";
const ICON = "clamp(64px, 10vw, 100px)"; // 64px floor keeps titles from squeezing into 3 lines at 320–360px
const TITLE_INSET = `calc(${PAD_X} + ${ICON} + ${GAP})`;



function Toggle({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 26,
        height: 26,
        position: "relative",
        flexShrink: 0,
        transition: "transform var(--t-smooth, 400ms) var(--ease-out, ease)",
        transform: open ? "rotate(45deg)" : "rotate(0deg)",
      }}
    >
      <span style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1.5, transform: "translateY(-50%)", background: "var(--terra)" }} />
      <span style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1.5, transform: "translateX(-50%)", background: "var(--terra)" }} />
    </span>
  );
}

/* ── Responsive screenshots ──────────────────────────────────────────────────
   These images are the heaviest thing on every page that uses this component.
   Measured on the Sales page before this existed: six screenshots at ~1640
   wide, 620 KB, and a 390px phone downloaded every one of them at full size
   for a 298px-wide box. Roughly a 5x overdraw on the connection least able to
   afford it.

   THE LADDER STOPS AT 1200 ON PURPOSE, and the native file is deliberately NOT
   a candidate. The box never renders wider than 673 CSS px (measured: it peaks
   at a 1440 viewport and shrinks slightly above 1680), so a retina desktop
   asks for 1346px and gets 1200 — 1.78x density instead of 2x, which no one
   can see in a 673px box, for about a third off the largest file. Adding a
   native rung would mean declaring each image's true width, which differs per
   file (1264 to 1646), and a wrong `w` descriptor makes the browser choose
   worse, not better. `src` stays the native file as the no-srcset fallback and
   as the honest source of record.

   INVARIANT: every image on a CoworkBuildItem MUST ship -480, -800 and -1200
   beside it. A srcset candidate that 404s does NOT fall back to `src` — the
   browser fails the image and the card renders empty. All 20 build-item images
   across the Cowork, Code, Design and Sales pages satisfy this as of
   2026-09-09; re-check after adding an image, because nothing enforces it at
   build time. */
const IMG_WIDTHS = [480, 800, 1200] as const;

/* Matches the measured box: 298px at a 390 viewport, 476 at 1024, 600 at 1280,
   673 at 1440 and above. The 46vw middle term is within 2% of the real column
   at every breakpoint between; the 680px cap over-declares the widest case by
   7px, which costs nothing. */
const IMG_SIZES = "(max-width: 767px) calc(100vw - 96px), (max-width: 1279px) 46vw, 680px";

function buildSrcSet(src: string): string | undefined {
  const dot = src.lastIndexOf(".");
  if (dot < 0) return undefined;
  const stem = src.slice(0, dot);
  const ext = src.slice(dot);
  return IMG_WIDTHS.map((w) => `${stem}-${w}${ext} ${w}w`).join(", ");
}

/* A muted loop that costs nothing until it is near the viewport: no src, so
   no bytes, until an IntersectionObserver sees it within 600px; then it loads
   and plays, and pauses again off screen. The card's still image sits under
   it, so there is never an empty frame. */
function LazyLoop({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const load = () => { if (!v.src) v.src = src; v.play().catch(() => {}); };
    if (!("IntersectionObserver" in window)) { load(); return; }
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) load(); else if (v.src) v.pause(); },
      { rootMargin: "600px 0px" },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [src]);
  return (
    <video
      ref={ref}
      className="cba-loop"
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
}

export function CoworkBuildAccordion({
  items,
  defaultOpen = 0,
  alwaysOpen = false,
}: {
  items: CoworkBuildItem[];
  defaultOpen?: number;
  /** Render every panel open, permanently — no height measurement, no resize
   *  listeners, and the header stops toggling. The Cowork/Sales pages already
   *  force exactly this shape from cowork-ds.css with `max-height: none
   *  !important`; the prop lets a page ask for it without the JS still
   *  measuring heights that CSS then throws away. Default false keeps the
   *  accordion behaviour everywhere else. */
  alwaysOpen?: boolean;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpen);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const panelRefSetters = useRef<((el: HTMLDivElement | null) => void)[]>([]);

  /* 2026-09-09 — what was wrong here:
     1. Each row shipped at `opacity: 0 / translateY(18px)` and faded in on its
        own IntersectionObserver (threshold 0.12, 600ms + 70ms stagger) while
        ALREADY nested inside a RevealSection doing the same thing. Two reveals
        on one element, and any row jumped past stayed blank. Rows render at
        full opacity now; the observer is gone.
     2. Panel height was measured by a `ref` callback that ran on EVERY render,
        reading scrollHeight in between inline max-height writes (layout
        thrash), with resize + orientationchange listeners re-measuring all six
        panels on top. Measurement now happens at the only two moments the
        height can change: once on mount for the initially open panel, and in
        the click handler. */

  /** Stable per-index ref setter — a fresh arrow per render would make React
   *  detach and re-attach every panel ref on every render. */
  const panelRef = (i: number) => {
    if (!panelRefSetters.current[i]) {
      panelRefSetters.current[i] = (el: HTMLDivElement | null) => {
        panelRefs.current[i] = el;
      };
    }
    return panelRefSetters.current[i];
  };

  const toggle = (i: number) => {
    if (alwaysOpen) return;
    const next = openIndex === i ? null : i;
    setOpenIndex(next);
    // Imperative, and the inline `maxHeight: 0` in the JSX never changes, so
    // React's style diff has nothing to write back over these values.
    panelRefs.current.forEach((el, idx) => {
      if (el) el.style.maxHeight = idx === next ? el.scrollHeight + "px" : "0px";
    });
  };

  // Mount only: open the starting panel without running its 500ms transition.
  useEffect(() => {
    if (alwaysOpen || defaultOpen == null) return;
    const el = panelRefs.current[defaultOpen];
    if (!el) return;
    const prev = el.style.transition;
    el.style.transition = "none";
    el.style.maxHeight = el.scrollHeight + "px";
    void el.offsetHeight; // flush, so restoring the transition can't animate it
    el.style.transition = prev;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <style>{`
        .cba-grid { padding-left: ${PAD_X}; padding-right: ${PAD_X}; }
        @media (min-width: 768px) { .cba-grid { padding-left: ${TITLE_INSET}; } }
        .cba-hl { position: relative; z-index: 0; display: inline-block; padding: 0 3px; margin: 0 -1px; }
        .cba-hl::before {
          content: "";
          position: absolute;
          left: -2px; right: -3px;
          top: 26%; bottom: -3%;
          /* Brighter, lighter terra wash (base terra #D94000 reads muddy at
             this size) — per Isaac 2026-07-02. */
          background: rgba(255, 122, 60, 0.28);
          border-radius: 7px 3px 8px 3px / 10px 4px 9px 5px;
          transform: scaleX(0) rotate(-0.6deg) skewX(-7deg);
          transform-origin: left center;
          transition: transform 650ms cubic-bezier(0.25, 0, 0.25, 1) 150ms;
          z-index: -1;
        }
        .cba-hl[data-show="true"]::before { transform: scaleX(1) rotate(-0.6deg) skewX(-7deg); }
        @media (prefers-reduced-motion: reduce) { .cba-hl::before { transition: none; } }
        /* Reduced motion: hide the loop and leave its poster still. */
        @media (prefers-reduced-motion: reduce) { .cba-loop { display: none; } }
      `}</style>
      {items.map((item, i) => {
        const isOpen = alwaysOpen || openIndex === i;
        return (
          <div
            key={i}
            className="cba-row"
            style={{
              background: CARD_BG,
              border: CARD_BORDER,
              borderRadius: RADIUS,
              overflow: "hidden",
            }}
          >
            {/* Header: icon · title · toggle */}
            <button
              type="button"
              className="w-full cursor-pointer"
              aria-expanded={isOpen}
              onClick={() => toggle(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: GAP,
                textAlign: "left",
                background: "none",
                border: "none",
                padding: `clamp(20px, 2.6vw, 28px) ${PAD_X} clamp(30px, 3.6vw, 44px)`,
              }}
            >
              {item.icon.startsWith(DS_ICON_PREFIX) ? (
                /* Cowork design system: a drawn mark, not a clay render.
                   Boxed to the clay icon's footprint so TITLE_INSET still
                   lines the body copy up under the title. The mark itself
                   renders at 52px — the system's ceiling for a drawn mark. */
                <span
                  aria-hidden="true"
                  style={{ width: ICON, height: ICON, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <DsIcon name={item.icon.slice(DS_ICON_PREFIX.length)} size={52} />
                </span>
              ) : (
                <img
                  src={item.icon}
                  alt=""
                  aria-hidden="true"
                  width={86}
                  height={86}
                  style={{ width: ICON, height: ICON, objectFit: "contain", flexShrink: 0 }}
                  loading="lazy"
                  decoding="async"
                />
              )}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="type-h4 text-charcoal">{item.headline}</span>
              </span>
              <Toggle open={isOpen} />
            </button>

            {/* Expanded: thin divider under the header, then copy left + screenshot
                right. Copy is inset to TITLE_INSET so it lines up with the title. */}
            <div
              ref={panelRef(i)}
              style={
                alwaysOpen
                  ? undefined
                  : { maxHeight: 0, overflow: "hidden", transition: "max-height 500ms cubic-bezier(0.4, 0, 0.2, 1)" }
              }
            >
              {/* Hairline divider — sits below the header, above the content */}
              <div style={{ borderTop: "1px solid var(--charcoal-14)", margin: `0 ${PAD_X}` }} />
              <div
                className="cba-grid grid grid-cols-1 md:[grid-template-columns:1fr_1.4fr]"
                style={{
                  gap: "clamp(22px, 3vw, 44px)",
                  alignItems: "start",
                  paddingTop: "clamp(26px, 3.2vw, 38px)",
                  paddingBottom: "clamp(32px, 3.8vw, 48px)",
                }}
              >
                <div style={{ maxWidth: "46ch" }}>
                  {item.description.split("\n\n").map((para, pi) => (
                    <p
                      key={pi}
                      className="type-body text-charcoal/75 leading-relaxed"
                      style={{ fontSize: "17px", margin: pi === 0 ? 0 : "14px 0 0" }}
                    >
                      <AnnotatedText text={para} show={isOpen} />
                    </p>
                  ))}
                </div>
                {item.media ? (
                  <div className="cba-media">{item.media}</div>
                ) : item.image && (
                <div style={{ position: "relative", width: "100%", aspectRatio: "3 / 2", borderRadius: 10, overflow: "hidden", border: "1px solid var(--charcoal-14)" }}>
                  <img
                    src={item.image}
                    srcSet={buildSrcSet(item.image)}
                    sizes={IMG_SIZES}
                    alt=""
                    aria-hidden="true"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                    loading="lazy"
                    decoding="async"
                  />
                  {item.video && <LazyLoop src={item.video} />}
                </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
